import React from "react";

import { IgAccordion } from "@intelligems/ig-theme/IgAccordion";
import { IgTypography } from "@intelligems/ig-theme/IgTypography";
import {
  DataType,
  GraphConfig,
} from "@intelligems/ig-types/schemas/analytics/custom-graphs";
import { Stack, useTheme } from "@mui/material";

import { MobileUpliftIndicator } from "../../shared/components/MobileUpliftIndicator";
import { formatMetricValue } from "../../shared/utils/mobileAnalyticsFormatters";

export type VariantData = {
  variation_id: string;
  name: string;
  value: number | null | undefined;
  uplift?: {
    value?: number;
    ci_low?: number;
    ci_high?: number;
    plus_minus: number;
  };
  order: number;
  isControl: boolean;
};

export type AudienceSegmentData = {
  segmentName: string;
  segmentValue: string;
  variations: VariantData[];
};

interface AudienceSegmentProps {
  segment: AudienceSegmentData;
  dataType: DataType;
  currency: { code: string; locale: string };
  graphConfig?: GraphConfig;
  showSegmentTitle: boolean;
}

/**
 * Accordion component displaying variation metrics for a single audience segment
 * Shows metric values and uplift indicators with appropriate coloring
 */
export const AudienceSegment: React.FC<AudienceSegmentProps> = ({
  segment,
  dataType,
  currency,
  graphConfig,
  showSegmentTitle,
}) => {
  const theme = useTheme();

  const accordionContent = (
    <Stack>
      {segment.variations.map((variant, i) => {
        let Uplift: React.ReactNode | null = null;
        if (
          !variant.isControl &&
          variant.uplift !== undefined &&
          variant.uplift !== null &&
          graphConfig?.impactCalculationColoring
        ) {
          Uplift = (
            <MobileUpliftIndicator
              uplift={variant.uplift}
              impactCalculationColoring={graphConfig.impactCalculationColoring}
              currency={currency}
            />
          );
        }
        return (
          <Stack key={i} pl={1}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              p={2}
            >
              <IgTypography variant="body2">{variant.name}</IgTypography>
              <Stack direction="column" alignItems={"flex-end"}>
                <IgTypography variant="body1" fontWeight={600}>
                  {formatMetricValue(variant.value, dataType, currency, "-")}
                </IgTypography>
                {Uplift}
              </Stack>
            </Stack>
          </Stack>
        );
      })}
    </Stack>
  );

  if (!showSegmentTitle) {
    return <Stack>{accordionContent}</Stack>;
  }

  // Otherwise, render with accordion
  return (
    <IgAccordion
      title={
        <IgTypography variant="body2" fontWeight={600}>
          {segment.segmentName}
        </IgTypography>
      }
      defaultExpanded={true}
      sx={{
        border: "none",
        borderRadius: 0,
        padding: 0,
        "::before": { display: "none" },
        "&:hover": {
          border: "none",
          borderShadow: "",
        },
        "& .MuiAccordionSummary-root": {
          backgroundColor: theme.igBackgroundColors.baseLightLow,
          borderRadius: "6px",
          paddingX: 2,
        },
      }}
    >
      {accordionContent}
    </IgAccordion>
  );
};
